/**
 * 도구 화면 동작. 땀 계산은 Web Worker(engine.worker.js), 파일 인코딩과 미리보기는 이 스크립트.
 * 입력한 글자는 서버로 보내지 않는다. 서버로 가는 것은 익명 통계(설정값)와 출시 알림 신청뿐.
 * 소개 페이지에서 넣은 글자는 주소 해시(#t=…&h=…)로 받아 입력 칸을 채우고, 바로 주소에서 지운다(src/shared/handoff.ts).
 */
import { fitView, renderPlan, type PlanLike, type View } from "./render";
import { encodePlan, type FormatId } from "../formats/index";
import type { StitchPlan, PlanStats } from "../engine/plan";
import type { FabricPreset, StitchParams } from "../engine/params";
import type { FontInfo } from "../shared/fonts";
import { parseHandoff } from "../shared/handoff";

interface Boot {
  fonts: FontInfo[];
  fabrics: FabricPreset[];
  defaults: StitchParams;
  swatches: { hex: string; name: string }[];
  fabricColors: { hex: string; name: string }[];
  version: string;
}

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
const boot: Boot = JSON.parse($("boot").textContent || "{}");

const worker = new Worker(`/static/engine.worker.js?v=${boot.version}`);
let seq = 0;
const pending = new Map<number, (m: any) => void>();
worker.onmessage = (e) => {
  const cb = pending.get(e.data.id);
  if (cb) {
    pending.delete(e.data.id);
    cb(e.data);
  }
};
function ask<T = any>(msg: Record<string, unknown>): Promise<T> {
  const id = ++seq;
  return new Promise((resolve) => {
    pending.set(id, resolve);
    worker.postMessage({ ...msg, id });
  });
}

/* ---------------- 입력 ---------------- */

const els = {
  text: $<HTMLTextAreaElement>("text"),
  font: $<HTMLSelectElement>("font"),
  fontNote: $("font-note"),
  height: $<HTMLInputElement>("height"),
  heightN: $<HTMLInputElement>("height-n"),
  spacing: $<HTMLInputElement>("spacing"),
  linegap: $<HTMLInputElement>("linegap"),
  color: $<HTMLInputElement>("color"),
  fabricNote: $("fabric-note"),
  density: $<HTMLInputElement>("density"),
  densityR: $<HTMLInputElement>("density-r"),
  pull: $<HTMLInputElement>("pull"),
  pullR: $<HTMLInputElement>("pull-r"),
  underlay: $<HTMLSelectElement>("underlay"),
  trimjump: $<HTMLInputElement>("trimjump"),
  smallstyle: $<HTMLSelectElement>("smallstyle"),
  resetAdv: $<HTMLButtonElement>("reset-adv"),
};

let color = boot.swatches[0]?.hex ?? "#1f2a44";

function radioValue(name: string): string {
  return (document.querySelector(`input[name="${name}"]:checked`) as HTMLInputElement | null)?.value ?? "";
}

function currentFabric(): FabricPreset {
  const id = radioValue("fabric") || "tshirt";
  return boot.fabrics.find((f) => f.id === id) ?? boot.fabrics[0];
}

function params(): Partial<StitchParams> {
  return {
    heightMm: Number(els.heightN.value),
    letterSpacingMm: Number(els.spacing.value),
    lineGap: Number(els.linegap.value) / 100,
    align: (radioValue("align") || "center") as StitchParams["align"],
    fabric: currentFabric().id,
    density: Number(els.density.value),
    pullComp: Number(els.pull.value),
    underlay: els.underlay.value as StitchParams["underlay"],
    trimJumpMm: Number(els.trimjump.value),
    smallStyle: els.smallstyle.value as StitchParams["smallStyle"],
  };
}

function link(range: HTMLInputElement, num: HTMLInputElement) {
  range.addEventListener("input", () => {
    num.value = range.value;
    schedule();
  });
  num.addEventListener("input", () => {
    range.value = num.value;
    schedule();
  });
}
link(els.height, els.heightN);
link(els.densityR, els.density);
link(els.pullR, els.pull);

// 소개 페이지·가이드에서 넘어온 글자·높이·원단 (해시). 채운 뒤 주소에서 지워, 새로 고침·방문 기록·공유 주소에 글자가 남지 않게 한다.
// 원단은 라디오만 고른다 — 새틴 간격·풀보정·언더레이 기본값은 맨 아래 applyFabricDefaults() 가 그 원단으로 채운다.
{
  const h = parseHandoff(location.hash);
  if (h) {
    if (h.text != null) els.text.value = h.text;
    if (h.heightMm != null) els.height.value = els.heightN.value = String(h.heightMm);
    if (h.fabric) {
      const r = document.querySelector<HTMLInputElement>(`input[name="fabric"][value="${h.fabric}"]`);
      if (r) r.checked = true;
    }
    history.replaceState(null, "", location.pathname + location.search);
  }
}

function applyFabricDefaults() {
  const f = currentFabric();
  els.density.value = els.densityR.value = String(f.density);
  els.pull.value = els.pullR.value = String(f.pullComp);
  els.underlay.value = f.underlay;
  els.fabricNote.textContent = f.note;
}

document.querySelectorAll<HTMLInputElement>('input[name="fabric"]').forEach((r) =>
  r.addEventListener("change", () => {
    applyFabricDefaults();
    schedule();
  }),
);
document.querySelectorAll<HTMLInputElement>('input[name="align"]').forEach((r) => r.addEventListener("change", schedule));
els.resetAdv.addEventListener("click", () => {
  applyFabricDefaults();
  schedule();
});
for (const el of [els.text, els.spacing, els.linegap, els.underlay, els.trimjump]) el.addEventListener("input", schedule);
els.underlay.addEventListener("change", schedule);
els.smallstyle.addEventListener("change", schedule);
els.font.addEventListener("change", () => {
  const f = boot.fonts.find((x) => x.id === els.font.value);
  els.fontNote.textContent = f ? `${f.note} (글꼴 파일 ${(f.bytes / 1048576).toFixed(1)}MB, 처음 한 번만 받습니다)` : "";
  schedule();
});

document.querySelectorAll<HTMLButtonElement>(".swatches button").forEach((b) =>
  b.addEventListener("click", () => {
    color = b.dataset.color || color;
    els.color.value = color;
    document.querySelectorAll(".swatches button").forEach((x) => x.setAttribute("aria-pressed", String(x === b)));
    draw();
    schedule();
  }),
);
els.color.addEventListener("input", () => {
  color = els.color.value;
  document.querySelectorAll(".swatches button").forEach((x) => x.setAttribute("aria-pressed", "false"));
  draw();
  schedule();
});

/* ---------------- 생성 ---------------- */

let plan: (StitchPlan & PlanLike) | null = null;
let stats: PlanStats | null = null;
let timer = 0;
let running = false;
let again = false;
let userMoved = false;
let lastBoxKey = "";

function schedule() {
  clearTimeout(timer);
  timer = window.setTimeout(run, 280);
}

async function run() {
  if (running) {
    again = true;
    return;
  }
  const text = els.text.value;
  if (!text.trim()) {
    plan = null;
    stats = null;
    showStats();
    draw();
    return;
  }
  running = true;
  $("busy").hidden = false;
  const r = await ask({ type: "generate", fontId: els.font.value, text, params: params(), color });
  running = false;
  $("busy").hidden = true;
  if (again) {
    again = false;
    run();
    return;
  }
  if (!r.ok) {
    showWarnings([r.error]);
    return;
  }
  plan = r.plan;
  stats = r.stats;
  showStats();
  showWarnings(r.warnings);
  const key = `${Math.round(stats!.widthMm)}x${Math.round(stats!.heightMm)}`;
  if (!userMoved || key !== lastBoxKey) fit();
  lastBoxKey = key;
  play.value = "1000";
  draw();
  document.querySelectorAll<HTMLButtonElement>(".dl button[data-format]").forEach((b) => (b.disabled = false));
  $("dl-name").textContent = `${fileBase()}.dst · ${stats!.widthMm.toFixed(1)}×${stats!.heightMm.toFixed(1)}mm`;
  $("title-url").textContent = `${text.split("\n")[0].slice(0, 20)} · ${params().heightMm}mm · ${currentFabric().short}`;
  track("generate", { stitches: stats!.stitches });
}

function showStats() {
  const s = stats;
  $("s-size").textContent = s ? `${s.widthMm.toFixed(1)} × ${s.heightMm.toFixed(1)}` : "-";
  $("s-st").textContent = s ? s.stitches.toLocaleString("ko-KR") : "-";
  $("s-trim").textContent = s ? String(s.trims) : "-";
  $("s-jump").textContent = s ? String(s.jumps) : "-";
  $("s-color").textContent = s ? String(s.colors) : "-";
  $("s-time").textContent = s ? (s.seconds < 60 ? `${Math.round(s.seconds)}초` : `${Math.floor(s.seconds / 60)}분 ${Math.round(s.seconds % 60)}초`) : "-";
}

function showWarnings(ws: string[]) {
  const ul = $("warns");
  ul.textContent = "";
  for (const w of ws) {
    const li = document.createElement("li");
    li.textContent = w;
    ul.appendChild(li);
  }
}

/* ---------------- 미리보기 ---------------- */

const cw = $("cw");
const cv = $<HTMLCanvasElement>("cv");
const ctx = cv.getContext("2d")!;
let view: View = { scale: 4, cx: 0, cy: 0 };
const play = $<HTMLInputElement>("play");
const viewSel = $<HTMLSelectElement>("view");
const jumps = $<HTMLInputElement>("show-jumps");
const fabricColor = $<HTMLSelectElement>("fabric-color");
let raf = 0;

function resize() {
  const dpr = window.devicePixelRatio || 1;
  const w = Math.max(10, Math.round(cw.clientWidth * dpr)),
    h = Math.max(10, Math.round(cw.clientHeight * dpr));
  if (cv.width !== w || cv.height !== h) {
    const kx = w / (cv.width || w);
    cv.width = w;
    cv.height = h;
    view = { scale: view.scale * kx, cx: view.cx * kx, cy: view.cy * kx };
    if (!userMoved) fit();
    draw();
  }
}
new ResizeObserver(resize).observe(cw);

function fit() {
  if (!plan) return;
  view = fitView(plan, cv.width, cv.height, 28 * (window.devicePixelRatio || 1));
  userMoved = false;
}

function draw() {
  cancelAnimationFrame(raf);
  raf = requestAnimationFrame(() => {
    const dpr = window.devicePixelRatio || 1;
    ctx.fillStyle = fabricColor.value;
    ctx.fillRect(0, 0, cv.width, cv.height);
    if (plan) {
      const n = plan.cmds.length;
      renderPlan(ctx, plan, view, {
        color,
        fabric: fabricColor.value,
        showJumps: jumps.checked,
        mode: viewSel.value === "path" ? "path" : "thread",
        upto: Math.round((n * Number(play.value)) / 1000),
      });
    }
    // 축척 막대
    const pxPerMm = view.scale / dpr;
    const nice = [1, 2, 5, 10, 20, 50, 100].find((mm) => mm * pxPerMm >= 40) ?? 100;
    $("scale-i").style.width = `${Math.round(nice * pxPerMm)}px`;
    $("scale-t").textContent = `${nice}mm`;
  });
}

function zoomAt(k: number, px: number, py: number) {
  const ns = Math.min(400, Math.max(0.3, view.scale * k));
  const kk = ns / view.scale;
  view = { scale: ns, cx: px - (px - view.cx) * kk, cy: py - (py - view.cy) * kk };
  userMoved = true;
  draw();
}

cv.addEventListener(
  "wheel",
  (e) => {
    e.preventDefault();
    const r = cv.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    zoomAt(Math.exp(-e.deltaY * 0.0015), (e.clientX - r.left) * dpr, (e.clientY - r.top) * dpr);
  },
  { passive: false },
);
const pointers = new Map<number, { x: number; y: number }>();
let pinchD = 0;
cv.addEventListener("pointerdown", (e) => {
  cv.setPointerCapture(e.pointerId);
  pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
  if (pointers.size === 2) {
    const [a, b] = [...pointers.values()];
    pinchD = Math.hypot(a.x - b.x, a.y - b.y);
  }
});
cv.addEventListener("pointermove", (e) => {
  const prev = pointers.get(e.pointerId);
  if (!prev) return;
  const dpr = window.devicePixelRatio || 1;
  pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
  if (pointers.size === 1) {
    view = { ...view, cx: view.cx + (e.clientX - prev.x) * dpr, cy: view.cy + (e.clientY - prev.y) * dpr };
    userMoved = true;
    draw();
  } else if (pointers.size === 2) {
    const [a, b] = [...pointers.values()];
    const d = Math.hypot(a.x - b.x, a.y - b.y);
    if (pinchD > 0) {
      const r = cv.getBoundingClientRect();
      zoomAt(d / pinchD, ((a.x + b.x) / 2 - r.left) * dpr, ((a.y + b.y) / 2 - r.top) * dpr);
    }
    pinchD = d;
  }
});
const endPointer = (e: PointerEvent) => {
  pointers.delete(e.pointerId);
  pinchD = 0;
};
cv.addEventListener("pointerup", endPointer);
cv.addEventListener("pointercancel", endPointer);

$("z-fit").addEventListener("click", () => {
  fit();
  draw();
});
$("z-in").addEventListener("click", () => zoomAt(1.4, cv.width / 2, cv.height / 2));
$("z-out").addEventListener("click", () => zoomAt(1 / 1.4, cv.width / 2, cv.height / 2));
$("z-real").addEventListener("click", () => {
  const dpr = window.devicePixelRatio || 1;
  const target = (96 / 25.4) * dpr; // CSS 1in = 96px
  zoomAt(target / view.scale, cv.width / 2, cv.height / 2);
});
for (const el of [viewSel, jumps, fabricColor]) el.addEventListener("change", draw);
play.addEventListener("input", draw);
let playing = 0;
$("play-btn").addEventListener("click", () => {
  if (playing) {
    cancelAnimationFrame(playing);
    playing = 0;
    $("play-btn").textContent = "재생";
    return;
  }
  const t0 = performance.now();
  const start = Number(play.value) >= 1000 ? 0 : Number(play.value);
  $("play-btn").textContent = "멈춤";
  const step = (t: number) => {
    const v = Math.min(1000, start + ((t - t0) / 8000) * 1000);
    play.value = String(v);
    draw();
    if (v < 1000) playing = requestAnimationFrame(step);
    else {
      playing = 0;
      $("play-btn").textContent = "재생";
    }
  };
  playing = requestAnimationFrame(step);
});

/* ---------------- 내려받기 ---------------- */

function fileBase(): string {
  const p = params();
  return `jasu-${p.fabric}-${String(p.heightMm).replace(".", "_")}mm`;
}

function save(data: Uint8Array, name: string, type = "application/octet-stream") {
  const blob = new Blob([data as BlobPart], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

document.querySelectorAll<HTMLButtonElement>(".dl button[data-format]").forEach((b) =>
  b.addEventListener("click", () => {
    if (!plan) return;
    const f = b.dataset.format as FormatId;
    const data = encodePlan({ ...plan, colors: [color] }, f, "JASU");
    save(data, `${fileBase()}.${f}`);
    track("download", { format: f, stitches: stats?.stitches ?? 0 });
    showAskAfter();
  }),
);

/** 내려받은 뒤 봉제 결과를 알려 달라는 한 줄을 보인다 */
function showAskAfter() {
  document.querySelectorAll<HTMLElement>("[data-ask-after]").forEach((el) => (el.hidden = false));
}

/* ---------------- 테스트 시트 ---------------- */

let sheetZip: Uint8Array | null = null;
const sheetCv = $<HTMLCanvasElement>("sheet-cv");
$("sheet-make").addEventListener("click", async () => {
  const sizes = [...document.querySelectorAll<HTMLInputElement>("#sheet-sizes input:checked")].map((x) => Number(x.value));
  const fabrics = [...document.querySelectorAll<HTMLInputElement>("#sheet-fabrics input:checked")].map((x) => x.value);
  const mode = radioValue("sheet-mode") === "grid" ? "grid" : "per";
  const btn = $<HTMLButtonElement>("sheet-make");
  btn.disabled = true;
  $("sheet-info").textContent = "만드는 중…";
  const r = await ask({ type: "sheet", fontId: els.font.value, text: els.text.value, params: params(), color, sizes, fabrics, mode });
  btn.disabled = false;
  if (!r.ok) {
    $("sheet-info").textContent = r.error;
    return;
  }
  sheetZip = r.zip;
  $<HTMLButtonElement>("sheet-dl").disabled = false;
  const s0 = r.sheets[0];
  const big = r.sheets.some((s: any) => s.stats.widthMm > 180 || s.stats.heightMm > 130);
  $("sheet-info").textContent = `${r.sheets.length}개 파일 · 첫 파일 ${s0.stats.widthMm.toFixed(0)}×${s0.stats.heightMm.toFixed(0)}mm · ${s0.stats.stitches.toLocaleString("ko-KR")}땀${big ? " · 18×13cm 틀보다 큽니다. 큰 틀을 쓰거나 원단마다 나눠 만드세요" : ""}`;
  $("sheet-cw").hidden = false;
  const dpr = window.devicePixelRatio || 1;
  sheetCv.width = sheetCv.clientWidth * dpr;
  sheetCv.height = sheetCv.clientHeight * dpr;
  const sctx = sheetCv.getContext("2d")!;
  renderPlan(sctx, r.preview, fitView(r.preview, sheetCv.width, sheetCv.height, 16 * dpr), { color, fabric: fabricColor.value, showJumps: false, mode: "thread" });
  // 칸 표
  const t = document.createElement("table");
  t.className = "t sheet-table";
  t.innerHTML = "<thead><tr><th>파일</th><th>원단</th><th class=num>높이</th><th class=num>크기(mm)</th><th class=num>땀 수</th></tr></thead>";
  const tb = document.createElement("tbody");
  for (const s of r.sheets)
    for (const c of s.cells) {
      const tr = document.createElement("tr");
      const fab = boot.fabrics.find((f) => f.id === c.fabric)?.short ?? c.fabric;
      for (const [v, cls] of [
        [s.file, ""],
        [fab, ""],
        [`${c.heightMm}mm`, "num"],
        [`${c.widthMm.toFixed(0)}×${c.heightTotalMm.toFixed(0)}`, "num"],
        [c.stitches.toLocaleString("ko-KR"), "num"],
      ]) {
        const td = document.createElement("td");
        td.textContent = v;
        if (cls) td.className = cls;
        tr.appendChild(td);
      }
      tb.appendChild(tr);
    }
  t.appendChild(tb);
  $("sheet-table").textContent = "";
  $("sheet-table").appendChild(t);
  track("sheet", { stitches: s0.stats.stitches });
});
$("sheet-dl").addEventListener("click", () => {
  if (!sheetZip) return;
  save(sheetZip, `jasu-test-sheet.zip`, "application/zip");
  track("download", { format: "zip" });
  showAskAfter();
});

/* ---------------- 출시 알림 (도구 화면) ---------------- */

const wl = document.querySelector<HTMLFormElement>("#notify form.wl");
wl?.addEventListener("submit", async (e) => {
  e.preventDefault();
  const fd = new FormData(wl);
  const body = Object.fromEntries([...fd.entries()].map(([k, v]) => [k, String(v)]));
  const res = await fetch("/api/waitlist", { method: "POST", headers: { "content-type": "application/json", accept: "application/json" }, body: JSON.stringify(body) });
  const j = (await res.json().catch(() => ({ ok: false }))) as { ok: boolean; rateLimited?: boolean; errors?: Record<string, string> };
  wl.querySelectorAll(".err").forEach((x) => x.remove());
  if (j.ok) {
    const p = document.createElement("p");
    p.className = "ok-msg";
    p.setAttribute("role", "status");
    p.textContent = "신청했습니다. 로고 자동 펀칭을 출시하면 이메일로 한 번 알려 드립니다.";
    wl.replaceWith(p);
    return;
  }
  const msg = j.rateLimited ? "잠시 뒤에 다시 신청해 주세요." : Object.values(j.errors ?? { e: "입력을 확인해 주세요." }).join(" ");
  const sp = document.createElement("span");
  sp.className = "err";
  sp.textContent = String(msg);
  wl.prepend(sp);
});

/* ---------------- 익명 통계 ---------------- */

let lastGenEvent = 0;
function track(kind: "generate" | "download" | "sheet", extra: Record<string, unknown>) {
  const now = Date.now();
  if (kind === "generate") {
    if (now - lastGenEvent < 15000) return; // 입력 중 연속 생성은 15초에 한 번만
    lastGenEvent = now;
  }
  const p = params();
  const text = els.text.value;
  const body = {
    kind,
    font: els.font.value,
    heightMm: p.heightMm,
    fabric: p.fabric,
    lines: Math.max(1, text.split("\n").filter((l) => l.trim()).length),
    chars: [...text.replace(/\s/g, "")].length,
    ...extra,
  };
  fetch("/api/event", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body), keepalive: true }).catch(() => {});
}

resize();
applyFabricDefaults();
run();
